import React, { useMemo, useCallback, useState, useContext, type CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import { Upload, Button, message, Image, ConfigProvider } from 'antd';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadProps as AntUploadProps, UploadFile } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { createCommand, parseChangeAction } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { deriveScopedComponentId, mergeStoreProps } from '../../base/storePropsUtils';
import { normalizeReactStyle, normalizeReactStyles, prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { upload as coreUpload, getAccessToken, getCoreConfig } from '../../config';
import type { UploadPreviewContext } from '../../config';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';

/** antd Upload 语义化 styles（root/list/item + 文档 5.3.0+ 的 trigger） */
export type AndUploadStyles = {
  root?: CSSProperties;
  list?: CSSProperties;
  item?: CSSProperties;
  trigger?: CSSProperties;
};

const UPLOAD_DOM_EXTRA_SKIP = [
  'repeatUpload',
  'buttonText',
  'previewAction',
  'fileList',
  'listDisplayText',
  'removeAction',
  'uploadParams',
] as const;

export interface AndUploadProps
  extends Omit<AntUploadProps, 'onChange' | 'styles'>,
    BaseComponentProps,
    DataSetConfig,
    ValidationDisplayConfig {
  /**
   * antd Upload 语义化 styles（root / list / item / trigger）；
   * 传给 Upload 时会去掉 `trigger`（仅 root/list/item），`trigger` 由内置默认按钮合并，避免与 antd 语义层重复。
   */
  styles?: AndUploadStyles | AntUploadProps['styles'];
  /**
   * 变更动作；仅在当前文件的 `status` 为 `done`（上传成功）或 `removed`（从列表移除）时派发。
   */
  changeAction?: string;
  /** 预览动作；点击列表预览图标时派发 command，参数见 UploadPreviewContext */
  previewAction?: string;
  /** 按钮文字 */
  buttonText?: string;
  /** 校验规则 */
  rules?: any[];
  /** 失焦时校验 */
  validateOnBlur?: boolean;
  /** 修改时校验 */
  validateOnChange?: boolean;
  /** 自定义变更处理（传入时由调用方完全接管；repeatUpload 的数量收敛与 store 写入不会执行） */
  onChange?: (info: any) => void;
  /** 上传地址 (如果不使用注入的 upload 方法) */
  uploadUrl?: string;
  /** 最大文件大小 (MB) */
  maxSize?: number;
  /** 上传参数配置 */
  uploadParams?: {
    form?: Record<string, any>;
    query?: Record<string, any>;
  };
  /** 列表类型为 text 且展示文件列表时，非空则每项显示该文案而非真实文件名 */
  listDisplayText?: string;
  /**
   * 为 true 时达到 maxCount 仍显示上传入口且不因数量拦截 beforeUpload；
   * 超过 maxCount 时新文件替换列表最后一项。
   * 仅在不传入自定义 onChange、走内置数据集 store 更新时生效。
   */
  repeatUpload?: boolean;
  /** 应用到 Upload 的 Design Token（antd theme.components.Upload），与 `styles` 配合编辑器配置 */
  token?: Record<string, unknown>;
  /** 迭代器内行下标，供 propsStore 作用域与 changeAction 中 @index 占位 */
  index?: number;
}

/** 克隆单项 UploadFile，供 antd 内部修改 thumbUrl/uid 等（避免 Immer 冻结对象报错） */
function cloneUploadFileItem(f: any): UploadFile {
  if (!f || typeof f !== 'object') return f as UploadFile;
  const copy: Record<string, any> = {};
  for (const key of Object.keys(f)) {
    const val = f[key];
    if (val instanceof File || val instanceof Blob) {
      copy[key] = val;
    } else if (Array.isArray(val)) {
      copy[key] = val.slice();
    } else if (val && typeof val === 'object') {
      copy[key] = { ...val };
    } else {
      copy[key] = val;
    }
  }
  return copy as UploadFile;
}

function cloneUploadFileList(list: UploadFile[] | undefined): UploadFile[] | undefined {
  if (!list?.length) return list;
  return list.map(cloneUploadFileItem);
}

/** 避免 antd UploadList 对 originFileObj 异步写 thumbUrl 落到只读/过期 file 引用 */
function sanitizeUploadFileListForAntd(
  list: UploadFile[] | undefined,
  listType: string | undefined,
): UploadFile[] | undefined {
  if (!list?.length) return list;
  const isPicture = ['picture-card', 'picture-circle', 'picture'].includes(listType ?? '');
  if (!isPicture) return cloneUploadFileList(list);
  return list.map((f) => {
    const copy = cloneUploadFileItem(f);
    delete copy.originFileObj;
    if (copy.thumbUrl === undefined) {
      copy.thumbUrl = typeof copy.url === 'string' ? copy.url : '';
    }
    return copy;
  });
}

export const AndUpload: React.FC<AndUploadProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  previewAction,
  buttonText = '上传文件',
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  onChange,
  fileList: propFileList,
  children,
  uploadUrl,
  maxSize = 10,
  uploadParams,
  listDisplayText,
  action,
  index,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;

  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );

  /** 与 AndSelect 一致：迭代器内 propsStore 使用带下标的作用域 id */
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) =>
    derivedPropsComponentId
      ? s.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField
      ? (s.getDataSet(dataSetKey, effectiveDataSetField) as
          | string[]
          | UploadFile[]
          | undefined)
      : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  // 从 fileList 提取 src 数组（response.src ?? src ?? url ?? thumbUrl）
  const fileListToSrcArray = useCallback((list: any[]): string[] => {
    return list
      .map((f: any) => f?.response?.src ?? f?.src ?? f?.url ?? f?.thumbUrl ?? '')
      .filter((s: string) => s && typeof s === 'string');
  }, []);

  // 将 src[] 转为 fileList，供 Upload 展示缩略图（url/thumbUrl 使用 viewRes 以附加 auth_token）
  const srcArrayToFileList = useCallback((srcs: string[]): UploadFile[] => {
    const viewRes = getCoreConfig().viewRes;
    const toDisplayUrl = (s: string) => (viewRes ? viewRes(s) : s) || s;
    return srcs.map((src, i) => {
      const displayUrl = toDisplayUrl(src);
      return {
        uid: `src-${i}-${src}`,
        name: src.split('/').pop() || src,
        url: displayUrl,
        thumbUrl: displayUrl,
        status: 'done' as const,
        response: { src },
      };
    });
  }, []);

  // fileList 需为可变的普通对象，否则 Ant Design Upload 内部赋值 thumbUrl 时会报 "object is not extensible"
  // url/thumbUrl 使用 viewRes 以附加 auth_token
  const toMutableFileList = useCallback((list: any[]): UploadFile[] => {
    const viewRes = getCoreConfig().viewRes;
    const toDisplayUrl = (s: string) => (s && viewRes ? viewRes(s) : s) || s;
    return list.map((f: any) => {
      const src = f?.response?.src ?? f?.src ?? f?.url ?? f?.thumbUrl ?? '';
      const copy = cloneUploadFileItem(f) as Record<string, any>;
      const displayUrl =
        typeof src === 'string' && src ? toDisplayUrl(src) : copy.url ?? copy.thumbUrl ?? '';
      if (typeof displayUrl === 'string' && displayUrl) {
        copy.url = displayUrl;
        copy.thumbUrl = displayUrl;
      } else if (copy.thumbUrl === undefined) {
        copy.thumbUrl = typeof copy.url === 'string' ? copy.url : '';
      }
      return copy as UploadFile;
    });
  }, []);

  const [pendingFileList, setPendingFileList] = useState<UploadFile[] | null>(null);

  const fileListFromStore = useMemo(() => {
    const raw = storeValue ?? (storeProps?.fileList as UploadFile[] | undefined) ?? propFileList;
    if (raw == null) return undefined;
    if (typeof raw === 'string' && raw) return srcArrayToFileList([raw]);
    if (!Array.isArray(raw)) return undefined;
    if (raw.length > 0 && typeof raw[0] === 'string') return srcArrayToFileList(raw as string[]);
    return toMutableFileList(raw);
  }, [storeValue, storeProps?.fileList, propFileList, srcArrayToFileList, toMutableFileList]);

  // 上传进行中优先用 pendingFileList（含已完成 + uploading），全部完成后再回退 fileListFromStore
  const fileList = pendingFileList ?? fileListFromStore ?? [];

  const mergedProps = useMemo(
    () =>
      mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined, {
        skipKeys: ['value', 'help', 'validateStatus', 'fileList', 'listDisplayText'],
      }),
    [restProps, storeProps]
  );

  const merged = mergedProps as Record<string, any>;
  const maxCount = merged.maxCount as number | undefined;
  const repeatUpload = merged.repeatUpload === true;
  const effectiveListType = merged.listType ?? restProps.listType;
  const effectivePreviewAction = String(merged.previewAction ?? previewAction ?? '').trim();
  const editorToken =
    merged.token && typeof merged.token === 'object' && !Array.isArray(merged.token)
      ? (merged.token as Record<string, unknown>)
      : undefined;
  const editorStyles = merged.styles as AntUploadProps['styles'] | undefined;

  /** `styles.trigger` 仅合并到 AndUpload 内置默认按钮；不随 `styles` 传给 Upload，避免双重生效 */
  const triggerStyle = useMemo((): CSSProperties | undefined => {
    const s = editorStyles;
    if (!s || typeof s === 'function' || Array.isArray(s)) return undefined;
    const t = (s as AndUploadStyles).trigger;
    return t && typeof t === 'object' && !Array.isArray(t)
      ? normalizeReactStyle(t as Record<string, unknown>)
      : undefined;
  }, [editorStyles]);

  /** 对象型 styles 去掉 trigger 再交给 Upload；函数型 styles 原样传入（无法剥离 trigger） */
  const uploadStylesWithoutTrigger = useMemo((): AntUploadProps['styles'] | undefined => {
    const s = editorStyles;
    if (!s || Array.isArray(s)) return undefined;
    if (typeof s === 'function') return s;
    const { trigger: _omit, ...rest } = s as AndUploadStyles;
    return normalizeReactStyles(rest as Record<string, unknown>) as AntUploadProps['styles'];
  }, [editorStyles]);

  const uploadSpreadProps = useMemo(
    () =>
      prepareAntdDomProps(mergedProps as Record<string, unknown>, UPLOAD_DOM_EXTRA_SKIP),
    [mergedProps],
  );

  // 上传前校验
  const beforeUpload = useCallback((file: File) => {
    const isWithinSize = file.size / 1024 / 1024 < maxSize;
    if (!isWithinSize) {
      message.error(`文件大小不能超过 ${maxSize}MB`);
      return false;
    }
    return true;
  }, [maxSize]);

  // 自定义上传请求 - 使用注入的 upload 方法
  const customRequest = useCallback(async (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;
    const config = getCoreConfig();
    
    // 如果配置了 upload 方法，使用它
    if (config.upload) {
      try {
        onProgress?.({ percent: 30 });
        const result = await coreUpload(file, { 
          url: uploadUrl,
          headers: {
            'Authorization': getAccessToken() ? `Bearer ${getAccessToken()}` : '',
          },
          form: uploadParams?.form,
          query: uploadParams?.query,
        });
        onProgress?.({ percent: 100 });
        onSuccess?.(result, file);
      } catch (error) {
        onError?.(error);
        message.error('上传失败');
      }
    } else {
      // 回退到默认上传行为
      onError?.(new Error('Upload handler not configured'));
      message.warning('上传功能未配置');
    }
  }, [uploadUrl, uploadParams]);

  const handleChange = useCallback(
    (info: any) => {
      if (onChange) {
        onChange(info);
        return;
      }
      let list = Array.isArray(info.fileList) ? toMutableFileList(info.fileList) : info.fileList;
      if (Array.isArray(list)) {
        list = sanitizeUploadFileListForAntd(list, effectiveListType) ?? list;
      }
      const hasUploading = Array.isArray(list) && list.some((f: any) => f?.status === 'uploading');
      if (
        repeatUpload &&
        typeof maxCount === 'number' &&
        Array.isArray(list) &&
        list.length > maxCount &&
        !hasUploading
      ) {
        list = sanitizeUploadFileListForAntd(
          toMutableFileList([...list.slice(0, maxCount - 1), list[list.length - 1]]),
          effectiveListType,
        ) ?? toMutableFileList([...list.slice(0, maxCount - 1), list[list.length - 1]]);
      }
      const srcArray = Array.isArray(list) ? fileListToSrcArray(list) : [];
      // 仅在 srcArray 有值或列表已清空时更新 store；上传中（response 未就绪）srcArray 为空，不可用 [] 覆盖
      const shouldUpdateStore =
        dataSetKey &&
        effectiveDataSetField &&
        (srcArray.length > 0 || !Array.isArray(list) || list.length === 0);
      // 上传中：用 pending 保留「已完成 + uploading」完整列表，且暂不写 store（避免只写入部分 url 后
      // 清空 pending 导致受控 fileList 回退为 1 项）。全部完成后一次性写入最终 srcArray。
      if (hasUploading) {
        flushSync(() => setPendingFileList(list));
      } else {
        flushSync(() => setPendingFileList(null));
        if (shouldUpdateStore) {
          setDataSet(dataSetKey, effectiveDataSetField, srcArray);
        }
      }
      if (validateOnChange) validate(list);
      if (changeAction && effectiveSessionId) {
        const changePayload = {
          file: info.file,
          fileList: list,
          src: srcArray,
        };
        const { actionName, arg } = parseChangeAction(changeAction, changePayload, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      onChange,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      validateOnChange,
      validate,
      changeAction,
      effectiveSessionId,
      id,
      effectiveDataSet,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      toMutableFileList,
      fileListToSrcArray,
      repeatUpload,
      maxCount,
      ctx,
      effectiveListType,
    ]
  );

  const handleBlur = useCallback(() => {
    if (validateOnBlur) validate(fileList);
  }, [validateOnBlur, validate, fileList]);

  // 获取预览 URL（移除 download=1）
  const getPreviewUrl = useCallback((file: UploadFile): string => {
    const url = file.url || file.thumbUrl;
    if (!url) return '';
    const src = (file.response as any)?.src ?? (typeof url === 'string' ? url : '');
    const viewRes = getCoreConfig().viewRes;
    const previewUrl = src && viewRes ? viewRes(src) : url;
    return String(previewUrl).replace(/([?&])download=1(&|$)/, (_, p1, p2) => (p2 ? p1 : ''));
  }, []);

  const isImageFile = useCallback((file: UploadFile): boolean => {
    const src = (file.response as any)?.src ?? file.name ?? '';
    const ext = String(src).split('.').pop()?.toLowerCase() || '';
    return ['webp', 'svg', 'png', 'gif', 'jpg', 'jpeg', 'jfif', 'bmp', 'ico', 'heic', 'heif'].includes(ext) ||
      /^data:image\//.test(getPreviewUrl(file));
  }, [getPreviewUrl]);

  const [previewState, setPreviewState] = useState<{ url: string; name: string; urls: string[]; current: number; open: boolean }>({
    url: '', name: '', urls: [], current: 0, open: false,
  });

  const handlePreview = useCallback(
    (file: UploadFile) => {
      const cleanUrl = getPreviewUrl(file);
      if (!cleanUrl) return;

      const list = fileList ?? [];
      const activeIndex = list.findIndex(
        (f) => (f.uid && f.uid === file.uid) || (f.url && f.url === file.url)
      );
      const resolvedIndex = activeIndex >= 0 ? activeIndex : 0;
      const fileSrc = (file.response as any)?.src ?? file.url ?? file.thumbUrl;
      const previewContext: UploadPreviewContext = {
        file: file as Record<string, any>,
        fileList: list as Record<string, any>[],
        activeIndex: resolvedIndex,
        previewUrl: cleanUrl,
        src: typeof fileSrc === 'string' ? fileSrc : undefined,
        id,
        sessionId: effectiveSessionId,
        dataSet: effectiveDataSet,
        dataSetField: effectiveDataSetField,
        index: effectiveIndex,
        parentIndex: effectiveParentIndex,
      };

      if (effectivePreviewAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(
          effectivePreviewAction,
          previewContext,
          {
            id,
            dataSet: effectiveDataSet,
            dataSetField: effectiveDataSetField,
            index: effectiveIndex,
            parentIndex: effectiveParentIndex,
          },
          ctx
        );
        addCommand(effectiveSessionId, createCommand(actionName, arg));
        return;
      }

      const defaultPreview = getCoreConfig().defaultUploadViewAction;
      if (defaultPreview) {
        defaultPreview(previewContext);
        return;
      }

      if (isImageFile(file) && list.length) {
        const imageFiles = list.filter((f) => isImageFile(f));
        const urls = imageFiles.map((f) => getPreviewUrl(f)).filter(Boolean);
        const idx = imageFiles.findIndex(
          (f) => (f.uid || f.url) === (file.uid || file.url)
        );
        setPreviewState({
          url: cleanUrl,
          name: file.name,
          urls,
          current: idx >= 0 ? idx : 0,
          open: true,
        });
      } else if (!isImageFile(file)) {
        window.open(cleanUrl, '_blank');
      }
    },
    [
      getPreviewUrl,
      isImageFile,
      fileList,
      effectivePreviewAction,
      effectiveSessionId,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      ctx,
    ]
  );

  const config = getCoreConfig();
  const useCustomUpload = !!config.upload && !action;

  const effectiveButtonText = merged.buttonText ?? buttonText;
  const effectiveListDisplayText = String(storeProps?.listDisplayText ?? listDisplayText ?? '').trim();
  const uploadFileList = useMemo(() => {
    if (!fileList?.length) {
      return [];
    }
    let list = sanitizeUploadFileListForAntd(fileList, effectiveListType) ?? fileList;
    const showUploadList = merged.showUploadList;
    if (
      effectiveListType === 'text' &&
      showUploadList !== false &&
      effectiveListDisplayText
    ) {
      list = list.map((f) => ({
        ...cloneUploadFileItem(f),
        name: effectiveListDisplayText,
      }));
    }
    return list;
  }, [fileList, effectiveListType, merged.showUploadList, effectiveListDisplayText]);
  const currentCount = fileList?.length ?? 0;
  const reachMaxCount = typeof maxCount === 'number' && currentCount >= maxCount;

  // 上传前校验：已达最大数量时拒绝（repeatUpload 时放行数量限制）
  const beforeUploadWithMaxCount = useCallback(
    (file: File) => {
      if (reachMaxCount && !repeatUpload) {
        message.warning(`最多只能上传 ${maxCount} 个文件`);
        return false;
      }
      return beforeUpload(file);
    },
    [reachMaxCount, repeatUpload, maxCount, beforeUpload]
  );

  const showUploadTrigger = !reachMaxCount || repeatUpload;
  const uploadEl = (
    <Upload
      {...uploadSpreadProps}
      styles={uploadStylesWithoutTrigger}
      disabled={develop ? false : mergedProps.disabled}
      fileList={uploadFileList}
      onChange={handleChange}
      onPreview={handlePreview}
      beforeUpload={beforeUploadWithMaxCount}
      customRequest={useCustomUpload ? customRequest : undefined}
      action={useCustomUpload ? undefined : (action || uploadUrl)}
      openFileDialogOnClick={!develop}
    >
      {showUploadTrigger &&
        (children ||
          (effectiveListType === 'picture-card' || effectiveListType === 'picture-circle' ? (
            <button
              type="button"
              style={{ border: 0, background: 'none', ...triggerStyle }}
            >
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>{effectiveButtonText}</div>
            </button>
          ) : (
            <Button icon={<UploadOutlined />} style={triggerStyle}>
              {effectiveButtonText}
            </Button>
          )))}
    </Upload>
  );

  return (
    <div onBlur={handleBlur}>
      {editorToken && Object.keys(editorToken).length > 0 ? (
        <ConfigProvider theme={{ components: { Upload: editorToken } }}>{uploadEl}</ConfigProvider>
      ) : (
        uploadEl
      )}
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
      {previewState.url && (
        <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
          {previewState.urls.length > 1 ? (
            <Image.PreviewGroup
              preview={{
                current: previewState.current,
                onOpenChange: (open) => !open && setPreviewState((p) => ({ ...p, open: false })),
              }}
            >
              {previewState.urls.map((url, i) => (
                <Image
                  key={i}
                  src={url}
                  style={{ width: 1, height: 1 }}
                  preview={{
                    open: previewState.open && previewState.current === i,
                    onOpenChange: (v) => !v && setPreviewState((p) => ({ ...p, open: false })),
                  }}
                />
              ))}
            </Image.PreviewGroup>
          ) : (
            <Image
              src={previewState.url}
              alt={previewState.name}
              style={{ width: 1, height: 1 }}
              preview={{
                open: previewState.open,
                onOpenChange: (open) => !open && setPreviewState((p) => ({ ...p, open: false })),
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AndUpload;

